/* ═══════════════════════════════════════════════════════════════════════
   Moonwater — BOGO Engine (campaign module, toggle-able)
   assets/mw-bogo-engine.js
   Load in <head> AFTER mw-cart-store.js:
     <script src="{{ 'mw-cart-store.js' | asset_url }}"></script>
     {%- if settings.enable_bogo -%}
       <script>window.MW_BOGO_ENABLED = true;</script>
       <script src="{{ 'mw-bogo-engine.js' | asset_url }}"></script>
     {%- endif -%}
   The config block (window.MW_BOGO with qualifying variants / flavors /
   default variant) is still emitted by mw-bogo-block.liquid as before.

   ──────────────────────────────────────────────────────────────────────
   WHAT THIS IS
   A self-contained campaign module. When enabled it subscribes to the
   unified cart store and keeps BOGO pairs correct: every qualifying paid
   pack gets a free partner, orphans are cleaned up, extras deduped, free
   quantities matched to paid. Runs GLOBALLY (head-loaded) so a one-click
   BOGO add from a product page forms its pair before the cart is opened.

   WHY IT'S SAFE TO TOGGLE
   If window.MW_BOGO_ENABLED is not true, this file does NOTHING — it
   returns before subscribing. No sync, no writes, no DOM, no influence on
   the cart. Flip the theme setting off and the cart behaves as if BOGO
   never existed. To retire permanently, remove the include + the pair
   snippet; the core cart is untouched.

   WHY NO fetch-wrap / NO MutationObserver (vs the old mw-bogo-block)
   Those were the feedback loop. All writes now go through MWCart.batch
   (atomic, serialized, XHR) and all reads come from store broadcasts.
   Pairing can never race a user removal into a 400, because the queue
   runs writes one at a time.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ── Master toggle. Off = total no-op. ──────────────────────────────
  if (window.MW_BOGO_ENABLED !== true) return;
  if (window.__mwBogoEngine) return;          // singleton
  window.__mwBogoEngine = true;

  var PROP_PAIR_ID = '_bogo_pair_id';
  var PROP_FREE    = '_bogo_free';

  function cfg() {
    return window.MW_BOGO || { active: false, qualifyingVariants: [], flavors: [], defaultVariantId: null, value: '' };
  }
  function isQualifying(variantId) {
    return cfg().qualifyingVariants.indexOf(Number(variantId)) > -1;
  }
  function newPairId() {
    return 'bogo_' + Math.random().toString(36).slice(2, 11);
  }

  // Guard so our own sync-driven writes don't immediately re-trigger a
  // second sync pass before the first settles. The store serializes
  // writes, but this avoids redundant no-op passes.
  var syncing = false;

  /* ── Core sync: given a cart, compute the pairing operations needed,
     then run them as ONE atomic batch. Faithful port of the original
     syncBogo decision table (see inventory in build notes). Returns a
     promise; resolves when the cart is settled (or immediately if no-op). */
  function reconcile(cart) {
    var c = cfg();
    if (!c.active || !c.qualifyingVariants.length) return Promise.resolve();
    if (syncing) return Promise.resolve();

    var pairs = {};           // pairId -> { paid, frees: [] }
    var orphanFree = [];
    var unpairedPaid = [];

    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      var pid = props[PROP_PAIR_ID];
      var isFree = !!props[PROP_FREE];
      var isBundle = !!props._bundle_id;
      var isSub = !!item.selling_plan_allocation;

      if (isFree) {
        if (!pid) { orphanFree.push(item); return; }
        if (!pairs[pid]) pairs[pid] = { frees: [] };
        pairs[pid].frees.push(item);
        return;
      }
      if (isBundle || isSub) return;          // bundles & subs never pair
      if (!isQualifying(item.variant_id)) return;

      if (!pid) { unpairedPaid.push(item); return; }
      if (!pairs[pid]) pairs[pid] = { frees: [] };
      pairs[pid].paid = item;
    });

    // Build the operation list
    var ops = [];

    orphanFree.forEach(function (f) { ops.push({ op: 'remove', key: f.key }); });

    Object.keys(pairs).forEach(function (pid) {
      var p = pairs[pid];
      var frees = p.frees || [];
      if (!p.paid && frees.length > 0) {
        // frees with no paid partner -> remove them all
        frees.forEach(function (f) { ops.push({ op: 'remove', key: f.key }); });
      } else if (p.paid && frees.length === 0) {
        // paid with no free -> add the free partner
        ops.push({ op: 'addFree', variantId: p.paid.variant_id, quantity: p.paid.quantity, pairId: pid });
      } else if (p.paid && frees.length === 1) {
        // exactly one free -> match its qty to paid
        if (frees[0].quantity !== p.paid.quantity) {
          ops.push({ op: 'changeFree', key: frees[0].key, quantity: p.paid.quantity, pairId: pid, props: frees[0].properties });
        }
      } else if (p.paid && frees.length > 1) {
        // more than one free -> keep the user's chosen flavor (the one
        // differing from paid, else the last), remove the rest, match qty
        var keep = null;
        for (var i = 0; i < frees.length; i++) {
          if (frees[i].variant_id !== p.paid.variant_id) { keep = frees[i]; break; }
        }
        if (!keep) keep = frees[frees.length - 1];
        frees.forEach(function (f) {
          if (f.key !== keep.key) ops.push({ op: 'remove', key: f.key });
        });
        if (keep.quantity !== p.paid.quantity) {
          ops.push({ op: 'changeFree', key: keep.key, quantity: p.paid.quantity, pairId: pid, props: keep.properties });
        }
      }
    });

    unpairedPaid.forEach(function (paid) {
      var pid = newPairId();
      ops.push({ op: 'tagPaid', key: paid.key, quantity: paid.quantity, pairId: pid, props: paid.properties || {} });
      ops.push({ op: 'addFree', variantId: paid.variant_id, quantity: paid.quantity, pairId: pid });
    });

    if (!ops.length) return Promise.resolve();

    // Translate the operation list into applyChanges format. All key-based
    // changes (remove/changeFree/tagPaid) resolve to line numbers safely;
    // adds go by variant id. This is what fixes the duplicate same-variant
    // 400 — change.js by key collides on identical variants, by line it
    // doesn't, and applyChanges re-reads between writes so numbers stay valid.
    var changes = ops.map(function (op) {
      if (op.op === 'remove') {
        return { key: op.key, quantity: 0 };
      } else if (op.op === 'addFree') {
        return { addVariant: op.variantId, quantity: op.quantity, properties: keyedProps(c.value, op.pairId, true) };
      } else if (op.op === 'changeFree') {
        return { key: op.key, quantity: op.quantity, properties: mergeProps(op.props, c.value, op.pairId, true) };
      } else if (op.op === 'tagPaid') {
        return { key: op.key, quantity: op.quantity, properties: mergeProps(op.props, null, op.pairId, false) };
      }
    }).filter(Boolean);

    syncing = true;
    return window.MWCart.applyChanges(changes).then(function (settled) {
      syncing = false;
      return settled;
    }).catch(function (e) {
      syncing = false;
      console.error('[BOGO] reconcile failed', e);
    });
  }

  // Build free-pack properties { _bogo_free: value, _bogo_pair_id: pid }
  function keyedProps(value, pid, isFree) {
    var p = {};
    if (isFree) p[PROP_FREE] = value;
    p[PROP_PAIR_ID] = pid;
    return p;
  }
  // Merge existing props with bogo tags (preserves any other line props)
  function mergeProps(existing, value, pid, isFree) {
    var p = {};
    if (existing) for (var k in existing) if (existing.hasOwnProperty(k)) p[k] = existing[k];
    if (isFree && value != null) p[PROP_FREE] = value;
    p[PROP_PAIR_ID] = pid;
    return p;
  }

  /* ── Public add helper for the product page's one-click BOGO add.
     Other code calls MWBogo.addPair(variantId, qty); the engine adds the
     paid pack via the store, then reconcile() attaches the free partner
     on the resulting broadcast. */
  window.MWBogo = {
    addPair: function (variantId, quantity) {
      return window.MWCart.add({ id: Number(variantId), quantity: quantity || 1 });
      // free partner is attached by reconcile() on the broadcast that follows
    },
    reconcile: function () { return reconcile(window.MWCart.get()); },
    isActive: function () { return cfg().active && cfg().qualifyingVariants.length > 0; }
  };

  /* ── Re-render the drawer after a store-driven BOGO interaction. Uses
     the drawer's own surgical re-render if present, else a no-op (the
     store broadcast will have updated header/bar regardless). ── */
  function rerenderDrawer() {
    if (typeof window.mwRerenderDrawer === 'function') {
      window.mwRerenderDrawer();
    }
  }

  /* ── BOGO pair remove → atomic batch removing every line in the pair ──
     This is the path that previously threw 400s by racing syncBogo. Now
     it's one atomic batch on the serial queue; nothing else can interleave. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-mw-bogo-remove]');
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest('.mw-bogo-pair');
    if (!wrap) return;
    var pid = wrap.dataset.pairId;
    if (!pid) return;
    wrap.style.opacity = '0.5';

    // Read settled state, collect every line carrying this pair id, remove
    // them via removeKeys (resolves to line numbers, removes highest-first —
    // robust when the paid + free packs share a variant id, which is what
    // was causing the 400 with key-based batch removal).
    var cart = window.MWCart.get();
    var keys = (cart && cart.items || []).filter(function (i) {
      return i.properties && i.properties[PROP_PAIR_ID] === pid;
    }).map(function (i) { return i.key; });
    if (!keys.length) { wrap.style.opacity = ''; return; }

    window.MWCart.removeKeys(keys).then(function () {
      rerenderDrawer();
    }).catch(function (err) {
      console.error('[BOGO] pair remove failed', err);
      wrap.style.opacity = '';
    });
  });

  /* ── BOGO qty +/- → change the PAID line qty; reconcile matches free ──
     Keeps existing behavior: bumping qty scales the pair. reconcile()
     runs on the resulting broadcast and matches the free pack quantity
     to the paid quantity. */
  document.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-mw-bogo-qty-minus]');
    var plus  = e.target.closest('[data-mw-bogo-qty-plus]');
    if (!minus && !plus) return;
    e.preventDefault();
    var item = (minus || plus).closest('[data-key]');
    if (!item) return;
    var key = item.dataset.key;
    var valueEl = item.querySelector('[data-mw-bogo-qty-value]');
    var current = parseInt((valueEl && valueEl.textContent) || '1', 10);
    var next = minus ? Math.max(0, current - 1) : current + 1;
    var wrap = item.closest('.mw-bogo-pair');
    if (wrap) wrap.style.opacity = '0.6';

    window.MWCart.change({ id: key, quantity: next })
      .then(function () { rerenderDrawer(); })
      .catch(function (err) {
        console.error('[BOGO] qty change failed', err);
        if (wrap) wrap.style.opacity = '';
      });
  });

  /* ── BOGO flavor swap → remove old free, add new free (same pair id) ──
     One atomic batch so the swap can't leave a half-state or race. */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-mw-bogo-flavor-swap]');
    if (!sel) return;
    var newVariantId = Number(sel.value);
    var wrap = sel.closest('.mw-bogo-pair');
    var item = sel.closest('[data-free-variant]');
    if (!wrap || !item) return;
    var pid = wrap.dataset.pairId;
    var oldKey = item.dataset.key;
    sel.disabled = true;
    wrap.style.opacity = '0.6';

    var cart = window.MWCart.get();
    var oldFree = (cart && cart.items || []).filter(function (i) { return i.key === oldKey; })[0];
    var qty = oldFree ? oldFree.quantity : 1;

    // Remove old free via removeKeys (robust), then add the new flavor.
    window.MWCart.removeKeys([oldKey]).then(function () {
      return window.MWCart.add({
        id: newVariantId,
        quantity: qty,
        properties: keyedProps(cfg().value, pid, true)
      });
    }).then(function () {
      rerenderDrawer();
    }).catch(function (err) {
      console.error('[BOGO] flavor swap failed', err);
      sel.disabled = false;
      wrap.style.opacity = '';
    });
  });

  /* ── Populate the flavor dropdowns from MW_BOGO.flavors ──────────────
     Runs on each broadcast AND after drawer re-renders. The drawer re-render
     is async (XHR), so a single call can fire before the fresh <select>
     exists. We retry a few times over ~1s to catch the re-rendered element
     whenever it lands. Idempotent via the data-populated flag, so retries
     that find an already-filled select are no-ops. */
  function populateFlavorDropdowns() {
    var c = cfg();
    var attempts = 0;
    function run() {
      attempts++;
      var sels = document.querySelectorAll('[data-mw-bogo-flavor-swap]');
      var anyEmpty = false;
      for (var s = 0; s < sels.length; s++) {
        var sel = sels[s];
        if (sel.dataset.populated === '1' && sel.options.length > 0) continue;
        var item = sel.closest('[data-free-variant]');
        var currentVariant = item ? Number(item.dataset.freeVariant) : null;
        sel.innerHTML = '';
        for (var fi = 0; fi < c.flavors.length; fi++) {
          var f = c.flavors[fi];
          var opt = document.createElement('option');
          opt.value = f.id;
          opt.textContent = f.name;
          if (f.id === currentVariant) opt.selected = true;
          sel.appendChild(opt);
        }
        sel.dataset.populated = '1';
      }
      // If a select exists but flavors config wasn't ready, retry a bit.
      if ((!c.flavors || !c.flavors.length || anyEmpty) && attempts < 5) {
        setTimeout(run, 200);
      }
    }
    run();
  }

  /* ── Subscribe to the store: reconcile pairs + (re)populate dropdowns.
     The store serializes our batch writes after the current change, so
     this is safe and loop-free. ── */
  function wire() {
    if (!window.MWCart) { setTimeout(wire, 50); return; }
    window.MWCart.subscribe(function (cart) {
      reconcile(cart);
      setTimeout(populateFlavorDropdowns, 80);
    });
    setTimeout(populateFlavorDropdowns, 200);

    // Expose so the drawer's re-render can call populate immediately after
    // injecting fresh HTML (deterministic, not timing-based).
    window.mwPopulateBogoFlavors = populateFlavorDropdowns;

    // Also listen for Dawn's native cart-update event (published by
    // product-form.js after a product-page add, by quick-view, etc.).
    // Native adds use Dawn's own fetch and never touch the store — without
    // this, a one-click BOGO add wouldn't pair. NOTE: Dawn exposes
    // window.subscribe globally but NOT window.PUB_SUB_EVENTS, so we
    // subscribe with the literal event string 'cart-update' (confirmed live).
    var t = null;
    (function bindCartUpdate() {
      if (typeof window.subscribe !== 'function') {
        setTimeout(bindCartUpdate, 50);
        return;
      }
      window.subscribe('cart-update', function (event) {
        if (event && event.source === 'mw-store') return;
        clearTimeout(t);
        t = setTimeout(function () { window.MWCart.refresh(); }, 60);
        // Fresh DOM may have new selects — populate after the render settles.
        setTimeout(populateFlavorDropdowns, 120);
      });
    })();
  }
  wire();

})();